import {
    journeyUpdated,
    sessionInitialised,
    jwtSet,
    jwtCleared,
    leadIdSet,
    journeyProgressReset,
} from '../redux/actions/journey';
import { APP_RESET } from '../redux/types/app';
import { clearRememberedUserType } from '../lib/userTypeParam';
import { logoutUser } from '../analytics';

let _store = null;

export const registerStore = (store) => {
    _store = store;
};

const getState = () => (_store ? _store.getState().journey : null);

const dispatch = (action) => {
    if (_store) _store.dispatch(action);
};

export const notifyJourney = (journey, meta) => {
    if (!journey) return;
    dispatch(journeyUpdated(journey, meta));
};

export const setSessionId = (sessionId) => {
    if (!sessionId) return;
    sessionStorage.setItem('sessionId', sessionId);
    dispatch(sessionInitialised(sessionId));
};

export const getSessionId = () => {
    const fromStore = getState() && getState().sessionId;
    return fromStore || sessionStorage.getItem('sessionId') || null;
};

export const setJwt = (token) => {
    if (!token) return;
    // Fresh login: no existing JWT means this is a new session (OTP verify),
    // not a token refresh. Clear verifiedEmail so a stale address from a
    // previous session (e.g. deleted-user re-login in the same tab) cannot
    // pre-fill the email field or bypass email OTP.
    if (!sessionStorage.getItem('jwt')) {
        sessionStorage.removeItem('verifiedEmail');
    }
    sessionStorage.setItem('jwt', token);
    dispatch(jwtSet(token));
    // Cache userId from the `euid` claim (Hashid-encoded, opaque string) —
    // Stage 3+ URL-facing endpoints want this, not the `sub` claim, which
    // stays plain/internal and must never be used to build a URL.
    const euid = decodeJwtEuid(token);
    if (euid) sessionStorage.setItem('userId', euid);
};

// Refresh token (EW-I6224 / CC-4). Issued with the access JWT on OTP-verify and
// rotated on every /auth/refresh call (one-time-use). sessionStorage only — the
// transport reads it to silently re-issue an expired access token so an active
// user is never logged out mid-journey.
export const setRefreshToken = (token) => {
    if (!token) return;
    sessionStorage.setItem('refreshToken', token);
};

export const getRefreshToken = () => sessionStorage.getItem('refreshToken') || null;

export const getJwt = () => {
    const fromStore = getState() && getState().jwt;
    return fromStore || sessionStorage.getItem('jwt') || null;
};

/**
 * True if the cached JWT is missing or its `exp` claim is in the past. Used
 * by the /dad route guard to short-circuit a doomed status() call and bounce
 * the user to /sessionExpire instead.
 */
export const isJwtExpired = (token) => {
    const t = token || getJwt();
    if (!t) return true;
    const payload = decodeJwtPayload(t);
    if (!payload || typeof payload.exp !== 'number') return false;
    // exp is seconds since epoch; add a 5s skew so we don't bounce a request
    // that is about to fire with a JWT that expires mid-flight.
    return payload.exp * 1000 <= Date.now() + 5000;
};

export const clearJwt = () => {
    sessionStorage.removeItem('jwt');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('verifiedEmail');
    dispatch(jwtCleared());
};

/**
 * Opaque encoded userId (string) from the JWT `euid` claim. Returns null
 * until a JWT is issued (Stage 2 OTP verify). Stage 3+ endpoints
 * (`/email/...`, `/pan/...`, etc.) stamp this as `userId` instead of the
 * leadId Guid. Do not source this from `sub` — that claim stays plain and
 * is framework-internal only, never URL-facing.
 */
export const getUserId = () => {
    const cached = sessionStorage.getItem('userId');
    if (cached) return cached;
    const euid = decodeJwtEuid(getJwt());
    if (euid) sessionStorage.setItem('userId', euid);
    return euid;
};

const decodeJwtPayload = (token) => {
    if (!token) return null;
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        // base64url → base64
        let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad) b64 += '='.repeat(4 - pad);
        const json = decodeURIComponent(
            atob(b64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(json);
    } catch (_) {
        return null;
    }
};

const decodeJwtEuid = (token) => {
    const payload = decodeJwtPayload(token);
    return payload && payload.euid ? String(payload.euid) : null;
};

export const setLeadId = (leadId) => {
    if (!leadId) return;
    sessionStorage.setItem('leadId', leadId);
    dispatch(leadIdSet(leadId));
};

export const getLeadId = () => {
    const fromStore = getState() && getState().leadId;
    return fromStore || sessionStorage.getItem('leadId') || null;
};

/**
 * EW-I6249 — the email the user verified this session (typed + OTP, or Google).
 * The new BE returns `Email = null` on GET /email for an already-OTP'd address
 * (it's stored hashed; PROD's `/proof/getEmail` returned the plaintext
 * `UserEmailID` for any verified email). Redux holds it in-session but is
 * in-memory only — wiped on any reload. Caching it in sessionStorage lets the
 * email-verify screen re-prefill the field on back-from-PAN (and after an
 * in-tab refresh), restoring the prefill source PROD used to get from the BE.
 * Cleared in clearJwt() when the session ends.
 */
export const setVerifiedEmail = (email) => {
    if (!email) return;
    sessionStorage.setItem('verifiedEmail', email);
};

export const getVerifiedEmail = () => sessionStorage.getItem('verifiedEmail') || null;

/**
 * Exit Journey reset. Wipes all client-side journey state so a fresh journey
 * re-fetches every detail from the backend with no stale cache:
 *   - clears the identity / session sessionStorage keys (jwt, userId,
 *     refreshToken, verifiedEmail, leadId, sessionId);
 *   - dispatches APP_RESET so every redux slice returns to its initialState
 *     (router preserved — see reducers/index.js).
 * Attribution / pre-journey keys (lead_source_auth, tId, …) are left intact.
 */
export const resetJourneyState = () => {
    sessionStorage.removeItem('jwt');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('verifiedEmail');
    sessionStorage.removeItem('leadId');
    sessionStorage.removeItem('sessionId');
    clearRememberedUserType();
    dispatch({ type: APP_RESET });
    logoutUser(); // MoEngage — journey reset, drop the identified user
};

/**
 * New-application reset. Clears the journey-PROGRESS half of the slice
 * (currentStage/currentState, journeyPath, postEsignFno, correction, document
 * locks) while leaving identity/session (sessionId, jwt, leadId) alone.
 *
 * Called at the top of `POST /registration/signup` — the one moment we know a
 * second, unrelated journey is beginning in a tab that may still be holding the
 * previous one. Unlike `resetJourneyState()` (which is only reached from
 * `isSessionInvalidated` error branches) this is on the happy path, and unlike
 * the backend it can actually clear the sticky fields: an envelope's
 * `postEsignFno: null` / `journeyPath: null` is read as "no opinion", never as
 * "clear it", so nothing the backend sends can undo a previous journey's value.
 */
export const resetJourneyProgress = () => {
    // The remembered `?usertype=` belongs to the previous journey too — a CRM
    // journey that ended in this tab must not label the new DIY one.
    clearRememberedUserType();
    dispatch(journeyProgressReset());
};

/**
 * Snapshot of the latest envelope.journey block — `{ currentState, dropCode,
 * progressPercent, journeyPath, isCompleted, currentStage }`. Read from the
 * journey reducer; null until the first envelope arrives.
 */
export const getJourney = () => {
    const s = getState();
    return s ? { ...s, sessionId: undefined, jwt: undefined, leadId: undefined } : null;
};

/**
 * Resolve the canonical SPA route for a journey envelope. Anchored on
 * `currentStage` (per backend `StageDefinitions`); falls back to
 * `currentState` substring matching for stages that share a screen.
 *
 * `applicationType` selects the route prefix:
 *   - 'direct' (DIY)            → /dad/...
 *   - 'crm'    (branch)         → /branch/...
 *   - 'pp'     (partner / BA)   → /ba/...
 *
 * Stages 1/2/3 (Registration, OTP, Email) all land on the email-verify
 * screen (post-OTP first screen). Returns null if no journey is available
 * — caller should fall back to the legacy `getPage()` mapper.
 */
// Stage Completion Lock — the only two states whose RESUME route is the completed
// stage's OWN page: PAN_VERIFIED shows the PAN-confirmed card, BANK_VERIFIED the
// verified-bank card (TKT-0056 / EW-I6361). That is correct for an ordinary refresh,
// and wrong after POST /auth/resync: a device only reaches /multiSession because
// ANOTHER session already owns that stage, and the backend now keeps it locked for a
// resync session (dev lead 22866530, 2026-09-23 — Reload used to land back on the
// verified bank card, where Verify re-ran a real Karza penny drop and returned 200).
// Landing there now could only ever 409, which is the loop the resync design exists to
// avoid, so route PAST the stage instead.
const RESYNC_PARKED_PAGE = { PAN_VERIFIED: 'pan', BANK_VERIFIED: 'bank' };
const RESYNC_FORWARD_PAGE = { PAN_VERIFIED: 'adhaar', BANK_VERIFIED: 'signature' };

export const routeAfterResync = (journey, applicationType) => {
    const resume = routeForJourney(journey, applicationType);
    const s = String((journey && journey.currentState) || '').toUpperCase();
    const parked = RESYNC_PARKED_PAGE[s];
    const forward = RESYNC_FORWARD_PAGE[s];
    // Override ONLY when the resume route really is that stage's own page. A lead in
    // PENDING_CORRECTION or an active correction session is sent to rejectionCorrection
    // by routeForJourney on purpose, and must not be dragged forward from here.
    if (parked && forward && resume && resume.toLowerCase() === `/dad/${parked}`.toLowerCase()) {
        return `/dad/${forward}`;
    }
    return resume;
};

// Pages a customer can legitimately be on while the cross-sell layer is open: the card
// container itself plus every product screen it hands off to. The /dad stage guard must not
// bounce these back to /dad/crossSell on a full page load — most importantly the SSP result
// pages the Epay return leg lands on, whose receipt would otherwise never be seen.
const CROSS_SELL_LAYER_PATHS = [
    '/dad/crosssell',
    '/dad/ssp', '/dad/sspsuccess', '/dad/sspfailure', '/dad/sspprocessing',
    '/dad/postesignfno', '/dad/fnoesignprocessing', '/dad/fnoesignresult', '/dad/onemoney',
];

export const isCrossSellLayerPath = (pathname) => {
    const p = String(pathname || '').toLowerCase().replace(/\/+$/, '');
    return CROSS_SELL_LAYER_PATHS.indexOf(p) !== -1;
};

export const routeForJourney = (journey, applicationType) => {
    if (!journey) return null;
    const at = String(applicationType || 'direct').toLowerCase();
    const prefix = '/dad';

    const s = String(journey.currentState || '').toUpperCase();
    const n = Number(journey.currentStage);

    // Rejection Correction module ("Almost there" page). A verifier-rejected
    // lead with only correctable reasons sits in PENDING_CORRECTION and lands on
    // the correction page. Additionally, while a correction session is active
    // (status envelope `correction.active`), the transient terminal-phase states
    // that eSign fail/abandon can leave the lead in must resume BACK to the
    // correction page (spec §8.3), not the journey personal-details screen.
    if (s === 'PENDING_CORRECTION') {
        return `${prefix}/rejectionCorrection`;
    }
    const correction = journey.correction;
    if (correction && correction.active) {
        const CORRECTION_ACTIVE_STATES = [
            'DETAILS_DONE', 'FINAL_VALIDATION', 'FINAL_VALIDATION_DONE',
            'AOF_GENERATED', 'ESIGN_INITIATED',
        ];
        if (CORRECTION_ACTIVE_STATES.indexOf(s) !== -1) {
            return `${prefix}/rejectionCorrection`;
        }
    }

    // Completed-stage state names (LeadState enum) — when state ends in
    // `_DONE`/`_VERIFIED`/`_GENERATED`/`_CREATED`/`ACTIVATED`, the named stage
    // is already FINISHED and FE must advance to the NEXT screen. This takes
    // precedence over the substring fallback below (which would otherwise
    // route LIVENESS_DONE back to /dad/livephoto, BANK_VERIFIED back to
    // /dad/bank, etc.). Authoritative table — keep in sync with
    // src/MO.Ekyc.Domain/Enums/LeadState.cs.
    // Verifier-terminal REJECTED is compliance-only now (fraud/negative-list/
    // sanctions/adverse-media). Correctable rejects sit in PENDING_CORRECTION and
    // are handled above. No stage rewind — show the Non-STP under-review screen.
    // (EW-I7265: the old stage-number fallback dumped correctable multi-item
    // rejects onto the journey stage screen; that legacy path is removed.)
    if (s === 'REJECTED') {
        return `${prefix}/nonSTP`;
    }

    const NEXT_AFTER_COMPLETED_STATE = {
        // Non-STP: verifier queue is pending — land on Thank You page, not Congratulations
        PENDING_VERIFIER:'nonSTP',
        // TKT-0022: post-eSign NON-STP lands in the verifier-review queue
        // (currentState=NON_STP_REVIEW) — same Thank-You/under-review screen.
        NON_STP_REVIEW:  'nonSTP',
        OTP_VERIFIED:    'verifyEmail',
        EMAIL_VERIFIED:  'pan',
        PAN_VERIFIED:    'pan',         // resume shows PAN Confirmed screen first; user clicks Proceed to advance
        DIGILOCKER_DONE: 'bank',
        // TKT-0056: BE now keeps a verified-bank lead at BANK_VERIFIED / stage 6
        // (resumePage "BANK") until the FIRST signature-capture submit flips it to
        // SIGNATURE_PENDING. So a BANK_VERIFIED lead must resume INTO the bank
        // stage (dadBank renders the Verify-Bank summary card via GET /bank/details
        // hasVerifiedBank=true), NOT jump to Signature. Forward progress past the
        // verified card is driven by mapNextRouteFromJourney's signature fallback.
        // (EW-I6361 / EW-I6364 / EW-I6630 — resume/refresh during Change-Bank.)
        BANK_VERIFIED:   'bank',
        SIGNATURE_DONE:  'livephoto',
        // Franchisee (application_type PP) only: BE parks the lead on the "Desired Client
        // Code" page right after LIVENESS_DONE is recorded (Stage 8 sub-step); confirm moves
        // it back to LIVENESS_DONE. Other verticals never see this state.
        CLIENT_CODE_PENDING: 'clientCode',
        LIVENESS_DONE:   'personalAndNominee',
        // Stage 10 income-proof has no separate page in the DAD flow — F&O users
        // upload via OneMoney AA modal *inside* Stage 9 (`TradeInFnO` popup),
        // non-F&O users skip income proof entirely. Transient BE states
        // (DETAILS_DONE, FINAL_VALIDATION, AOF_GENERATED) resume on personal details
        // so the Stage 9 poll loop can complete and drive eSign itself.
        DETAILS_DONE:    'personalAndNominee',
        // FINAL_VALIDATION is the state BE flips to after a successful
        // /journey/final-validation rollup. Both STP and NON_STP go to esign —
        // only the post-esign terminal page differs. Drop decisions surface
        // separately on journey.dropCode (handle at call-site, not here).
        FINAL_VALIDATION:'personalAndNominee',
        VERIFIER_APPROVED:'esign',
        // The eKYC 3.0 eSign vendor-picker screen (/dad/esign) is retired — the
        // flow auto-redirects to the vendor and returns via /dad/verifyEsign. A
        // lead resumed at AOF_GENERATED must land on the (prefilled) personal page,
        // where "Proceed to eSign" re-fires /esign/initiate (dadPersonal handles
        // the AOF_GENERATED short-circuit). Routing here to 'esign' would render
        // the dead picker and never initiate.
        AOF_GENERATED:   'personalAndNominee',
        ESIGN_INITIATED: 'personalAndNominee',
        // TKT-0022: an Aadhaar exact-match hard-block on the eSign callback sets
        // ESIGN_DATA_MISMATCH (stage stays 13). The customer is sent back to
        // Stage-9 personal-details to review (NOT dropped); the callback's
        // errorMessage is surfaced at the call site. Key off currentState here,
        // since currentStage is always 13 for eSign.
        ESIGN_DATA_MISMATCH: 'personalAndNominee',
        ESIGN_DONE:      'congratulation',
        ACCOUNT_CREATED: 'congratulation',
        ACTIVATED:       'congratulation',
    };

    // DIGILOCKER_SKIP: PAN verified is the entry state for bank (DigiLocker is bypassed).
    // Never resume to the PAN confirmed screen — the user has already proceeded past it.
    if (s === 'PAN_VERIFIED' && String(journey.journeyPath || '').toUpperCase() === 'DIGILOCKER_SKIP') {
        return `${prefix}/bank`;
    }

    // BANK_VERIFIED at stage >= 7 means the user has already proceeded past bank to the
    // signature page. Stage is advanced to 7 by GET /signature/details on first entry.
    // At stage 6 (still on bank page), keep the TKT-0056 behaviour of resuming into bank.
    if (s === 'BANK_VERIFIED' && n >= 7) {
        return `${prefix}/signature`;
    }

    // Post-eSign F&O activation (`journey.postEsignFno`, BE-driven). While an
    // F&O offer / AA income-proof fetch is still open after eSign, the customer
    // belongs on the F&O screens — NOT on the Congratulations page that
    // ESIGN_DONE / ACCOUNT_CREATED / ACTIVATED would otherwise pick below. This
    // also stops the /dad stage guard evicting the customer off an F&O screen on
    // reload / bfcache restore. Resolved values (DEFERRED, REQ_PUSHED, ACTIVATED)
    // and null/absent fall through to the existing behaviour untouched.
    // The F&O signal only ever describes a lead that has already SIGNED: the
    // backend raises it solely for ESIGN_DONE / NON_STP_REVIEW /
    // VERIFIER_APPROVED / ACCOUNT_CREATED / ACTIVATED (PostEsignFnoRouting
    // .EligibleLeadStates), every one of which is stage >= 13. A value sitting on
    // a pre-eSign journey is therefore always leftover state, never a real offer
    // — so ignore it rather than routing a stage-2 lead onto an F&O screen.
    const isPostEsign = journey.isCompleted === true || (!Number.isNaN(n) && n >= 13);
    // Cross-sell layer (2026-10): when the backend reports an open layer session, the
    // card container owns the post-account-creation screen. Checked before the legacy
    // F&O block — the backend never sends both for a capable client. An unknown status
    // is ignored (treated as no layer) so a newer backend cannot strand an older FE.
    const crossSellStatus = journey.crossSell
        ? String(journey.crossSell.status || '').toUpperCase() : '';
    if (isPostEsign && (crossSellStatus === 'PENDING' || crossSellStatus === 'IN_PROGRESS')) {
        return `${prefix}/crossSell`;
    }
    const fno = isPostEsign ? String(journey.postEsignFno || '').toUpperCase() : '';
    if (fno === 'OFFER_PENDING') return `${prefix}/postEsignFNO`;
    if (fno === 'AA_FETCHING') return `${prefix}/fnoEsignProcessing`;
    // REQ_HELD belongs with PROOF_RECEIVED. The backend writes PROOF_RECEIVED and
    // then REQ_HELD in two back-to-back saves, so PROOF_RECEIVED is observable for
    // microseconds and REQ_HELD is what any real poll sees. While REQ_HELD fell
    // through to the closing page, this guard evicted the customer off
    // /dad/fnoEsignProcessing before its 3s poll could run — so a customer whose
    // F&O request had just SUCCEEDED was sent to Congratulations having been told
    // nothing about it. The result screen auto-redirects on its own dwell timer,
    // so nobody is parked here.
    if (fno === 'PROOF_RECEIVED' || fno === 'REQ_HELD'
        || fno === 'ABANDONED' || fno === 'PROOF_FAILED') {
        return `${prefix}/fnoEsignResult`;
    }
    // TODO: RIISE_SIGNIN has no dedicated FE screen. The F&O screens themselves
    // treat it as "F&O step is done, go to the closing page" (see
    // redirectToClosingPage in dadPostEsignFNO / dadFNOEsignProcessing), so it
    // falls through to the normal closing page (congratulation / nonSTP) here
    // until a RiiSE sign-in screen exists.

    let page = null;
    if (NEXT_AFTER_COMPLETED_STATE[s]) {
        page = NEXT_AFTER_COMPLETED_STATE[s];
    }
    else if (n === 4) page = 'pan';
    else if (n === 5) page = 'Adhaar';
    else if (n === 6) page = 'bank';
    else if (n === 7) page = 'signature';
    else if (n === 8) page = 'livephoto';
    else if (n === 9) page = 'personalAndNominee';
    // Stage 10 collapsed into Stage 9 modal — never land on a standalone income-proof page.
    else if (n === 10) page = 'personalAndNominee';
    else if (n === 13) page = 'esign';
    else if (n === 15) page = 'congratulation';
    else if (s.includes('PAN')) page = 'pan';
    else if (s.includes('AADHAAR') || s.includes('DIGILOCKER')) page = 'Adhaar';
    else if (s.includes('BANK')) page = 'bank';
    else if (s.includes('LIVE') || s.includes('SELFIE')) page = 'livephoto';
    else if (s.includes('SIGNATURE')) page = 'signature';
    else if (s.includes('PERSONAL') || s.includes('NOMINEE')) page = 'personalAndNominee';
    // Income-proof state names map back to Stage 9 (the OneMoney AA modal lives there).
    else if (s.includes('INCOME')) page = 'personalAndNominee';
    else if (s.includes('ESIGN')) page = 'esign';
    else if (s.includes('CONGRAT') || s.includes('COMPLETED')) page = 'congratulation';
    else page = 'verifyEmail';

    return `${prefix}/${page}`;
};
